import { Col, Row } from 'antd';
import React from 'react';
import { useParams } from 'react-router';

import {
  useGetVariantDetailQuery,
  useGetVariantLinkoutsQuery,
} from '../../api/variantApi';
import AddReference from '../components/AddReference';
import CollapseHeaderForSections from '../components/CollapseHeaderForSections';
import CollapsePanel from '../components/CollapsePanel';
import DetailCard from '../components/DetailCard';
import ReferencesOverview from '../components/ReferencesOverview';
import VariantUpdatePrimaryTranscript from '../components/VariantUpdatePrimaryTranscript';
import { VARIANT_STAGE_TYPES } from '../constants';
import { useVariantStep } from '../hooks/use_variant_step';
import Interpretation from '../interpretation/Interpretation';
import GeneFactsPhenotypesSection from './GeneFactsPhenotypesSection';
import SamplesSection from './SamplesSection';
import VariantDatabasesSection from './VariantDatabasesSection';

const ClinicalObservation = () => {
  const params = useParams();

  // Read the selected variant straight from the RTK Query cache (populated
  // by the workflow loader) rather than a Redux selector.
  const { data: variant } = useGetVariantDetailQuery(
    { variant_id: params.vid },
    { skip: !params.vid },
  );

  const stageInfo = useVariantStep(VARIANT_STAGE_TYPES.CLINICAL_OBSERVATION);

  // Read linkouts straight from the query hook's cache rather than a selector
  const { data: linkouts } = useGetVariantLinkoutsQuery(
    { variant_id: params.vid },
    { skip: !params.vid },
  );

  return (
    <div className="clinical-obs">
      <VariantUpdatePrimaryTranscript />
      <Row gutter={[8, 8]} className="workflow-stage-content-container">
        <Col flex={'auto'} className="workflow-stage-left-col">
          <DetailCard>
            <SamplesSection />
          </DetailCard>
          <DetailCard>
            <CollapsePanel
              key={`gene_facts_${params.vid}`}
              title={
                <CollapseHeaderForSections title={'Gene facts: phenotypes'} />
              }
              panelKey="gene_facts"
              defaultActiveKey="gene_facts"
            >
              <>
                <GeneFactsPhenotypesSection />
              </>
            </CollapsePanel>
          </DetailCard>
          <DetailCard>
            <VariantDatabasesSection />
          </DetailCard>
          <DetailCard>
            <CollapsePanel
              title={
                <CollapseHeaderForSections
                  title={
                    'References: case-control-/in-vivo studies, casuistics, reviews'
                  }
                  style={{
                    width: '75%',
                  }}
                />
              }
              panelKey="references"
            >
              <AddReference
                stageType={VARIANT_STAGE_TYPES.CLINICAL_OBSERVATION}
              />
              {/* Linkouts */}
              <div className="interpretation__intervar-link">
                {linkouts?.pubmed_url && (
                  <a
                    href={linkouts.pubmed_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="interpretation__intervar-link__anchor"
                  >
                    PubMed{' '}
                  </a>
                )}
                {linkouts?.mastermind_url && (
                  <a
                    href={linkouts.mastermind_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="interpretation__intervar-link__anchor"
                  >
                    MM{' '}
                  </a>
                )}
                {linkouts?.google_scholar_url && (
                  <a
                    href={linkouts.google_scholar_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="interpretation__intervar-link__anchor"
                  >
                    Scholar{' '}
                  </a>
                )}
              </div>
              {params.vid && (
                <ReferencesOverview
                  variantId={params.vid}
                  filterByType={(type: string) => type !== 'functional study'}
                  stageType={VARIANT_STAGE_TYPES.CLINICAL_OBSERVATION}
                />
              )}
            </CollapsePanel>
          </DetailCard>
        </Col>

        {stageInfo && variant && (
          <Interpretation
            variant={variant}
            stepLabel="Clinical observation"
            stepStatus={stageInfo.status}
            stepId={stageInfo.id}
            stepType={stageInfo.stage_type}
          />
        )}
      </Row>
    </div>
  );
};

export default ClinicalObservation;
